/**
 * Button Handler Utilities
 * Extracted from ChatFlowController V1 for reuse in V2
 * Manages button click handlers and routing
 */

import type { Dispatch } from '@reduxjs/toolkit';

import { addMessage } from '../data-store/copilotSlice';
import { saveAccountConfigurationForEdit } from '../services';
import { addUserResponse } from '../services/messageService';

import STEP_CONFIGS from './stepConfigs';
import withStepContext from './stepContextUtils';
import {
  handleAccountHelpNo,
  handleEditNotAligningPrompt,
  handleFileInstructionsNo,
  handleFileInstructionsYes,
  handleStepHelpNo,
} from './stepHelpUtils';
import {
  handleUserInputSelection,
  skipAddAccess,
  skipFromRemoveAccess,
  skipToFinalConfirmation,
} from './userInputUtils';

interface ButtonContext {
  label?: string;
  stepName?: string;
}

interface ApiServices {
  initiateOnboard?: (payload: unknown) => Promise<unknown>;
  testConnection?: (payload: unknown) => Promise<unknown>;
  saveUseCaseConfiguration?: (payload: unknown) => Promise<unknown>;
}

interface ControllerInstance {
  handleUserPrompt?: (prompt: string) => void;
}

type ButtonHandler = (context?: ButtonContext) => void | Promise<void>;

/**
 * Get button handler for specific button ID
 */
export const getButtonHandler = (
  buttonId: string,
  dispatch: Dispatch,
  getState: () => unknown,
  services: ApiServices,
  callbacks: {
    showFinalConfirmation: () => void;
    handleConnectionHelpNo: () => Promise<void>;
    handleAllAccountHelpNo: () => Promise<void>;
    handleAddAccessUploadYes: () => void;
    handleRemoveAccessUploadYes: () => void;
    saveUseCaseConfiguration?: (payload: unknown) => Promise<unknown>;
  },
  controller?: ControllerInstance
): ButtonHandler | undefined => {
  const handlers: Record<string, ButtonHandler> = {
    connection_help_yes: (context?: ButtonContext) =>
      withStepContext(context, dispatch, getState, () =>
        handleUserInputSelection(dispatch, context)
      ),

    connection_help_no: async (context?: ButtonContext) =>
      withStepContext(context, dispatch, getState, async () => {
        try {
          await callbacks.handleConnectionHelpNo();
        } catch {
          // Handle error silently
        }
      }),

    // Create Account handlers
    all_account_help_no: async (context?: ButtonContext) =>
      withStepContext(context, dispatch, getState, async () => {
        await callbacks.handleAllAccountHelpNo();
      }),

    all_account_help_yes: (context?: ButtonContext) =>
      withStepContext(context, dispatch, getState, () =>
        handleUserInputSelection(dispatch, context)
      ),

    account_not_aliging_with_previous_instruction: (context?: ButtonContext) =>
      withStepContext(context, dispatch, getState, () => {
        addUserResponse(dispatch, 'No', 'userResponseNo');

        dispatch(
          addMessage({
            id: `account-helper-${Date.now()}`,
            type: 'ai_response',
            subType: 'threeButtonsHelperText',
            timestamp: Date.now(),
            buttons: [
              { label: 'Specify Prompt', action: 'account_not_aligning_yes' },
              {
                label: 'Upload Browser Recording',
                action: 'account_not_aligning_yes',
              },
              { label: 'Skip for Now', action: 'account_not_aligning_skip' },
            ],
          })
        );
      }),

    account_not_aligning_yes: (context?: ButtonContext) =>
      withStepContext(context, dispatch, getState, () => {
        addUserResponse(dispatch, 'Yes', 'userResponseYes');
        handleUserInputSelection(dispatch, context);
      }),

    account_not_aligning_no: (context?: ButtonContext) =>
      withStepContext(context, dispatch, getState, () => {
        addUserResponse(dispatch, 'No', 'userResponseNo');
        handleUserInputSelection(dispatch);
      }),

    account_not_aligning_skip: async (context?: ButtonContext) =>
      withStepContext(context, dispatch, getState, async () => {
        const state = getState() as {
          copilot?: {
            ui?: { endpointKey?: string };
            navigation?: { activeStep?: string };
            stepFormData?: Record<string, Record<string, unknown>>;
          };
        };

        const endpointKey = state.copilot?.ui?.endpointKey;
        const activeStep = state.copilot?.navigation?.activeStep;
        const formData = activeStep
          ? state.copilot?.stepFormData?.[activeStep]
          : undefined;

        if (endpointKey && formData && activeStep) {
          const jsonFieldMap: Record<string, string> = {
            CreateAccount: 'externalconnattvalue.CreateAccountJSON',
            UpdateAccount: 'externalconnattvalue.UpdateAccountJSON',
            EnableAccount: 'externalconnattvalue.EnableAccountJSON',
            DisableAccount: 'externalconnattvalue.DisableAccountJSON',
            RemoveAccount: 'externalconnattvalue.RemoveAccountJSON',
          };

          const jsonField = jsonFieldMap[activeStep];
          const accountJSON = formData[jsonField];

          if (jsonField && typeof accountJSON === 'string') {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment

            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            await saveAccountConfigurationForEdit(
              services,
              dispatch,
              getState,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              activeStep as any,
              accountJSON,
              Number(endpointKey)
            );
          }
        }
      }),

    continue: async (context?: ButtonContext) =>
      withStepContext(context, dispatch, getState, async () => {
        addUserResponse(dispatch, 'Yes', 'userResponseYes');

        const state = getState() as {
          copilot?: {
            ui?: { endpointKey?: string };
            navigation?: { activeStep?: string };
            stepFormData?: Record<string, Record<string, unknown>>;
            parsedTemplates?: Record<string, { instructions?: string }>;
          };
        };

        const endpointKey = state.copilot?.ui?.endpointKey;
        const activeStep = state.copilot?.navigation?.activeStep;
        const formData = activeStep
          ? state.copilot?.stepFormData?.[activeStep]
          : undefined;
        const existingPrompt = activeStep
          ? state.copilot?.parsedTemplates?.[activeStep]?.instructions ?? ''
          : '';

        if (endpointKey && formData && activeStep && existingPrompt) {
          const jsonFieldMap: Record<string, string> = {
            CreateAccount: 'externalconnattvalue.CreateAccountJSON',
            UpdateAccount: 'externalconnattvalue.UpdateAccountJSON',
            EnableAccount: 'externalconnattvalue.EnableAccountJSON',
            DisableAccount: 'externalconnattvalue.DisableAccountJSON',
            RemoveAccount: 'externalconnattvalue.RemoveAccountJSON',
          };

          const jsonField = jsonFieldMap[activeStep];
          const accountJSON = formData[jsonField];

          if (jsonField && typeof accountJSON === 'string') {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            await saveAccountConfigurationForEdit(
              services,
              dispatch,
              getState,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              activeStep as any,
              accountJSON,
              Number(endpointKey),
              existingPrompt
            );
          }
        }
      }),

    addaccess_upload_yes: () => callbacks.handleAddAccessUploadYes(),

    removeaccess_upload_yes: () => callbacks.handleRemoveAccessUploadYes(),

    skip_to_final: () =>
      skipToFinalConfirmation(dispatch, callbacks.showFinalConfirmation),

    skip_add_access: () =>
      skipAddAccess(dispatch, callbacks.showFinalConfirmation),

    skip_remove_access: () => skipFromRemoveAccess(dispatch, getState, 0),

    file_instructions_yes: (context?: ButtonContext) =>
      withStepContext(context, dispatch, getState, () => {
        handleFileInstructionsYes(dispatch, getState, controller);
      }),

    file_instructions_no: (context?: ButtonContext) =>
      withStepContext(context, dispatch, getState, () => {
        handleFileInstructionsNo(dispatch, getState);
      }),

    cancel: (context?: ButtonContext) =>
      withStepContext(context, dispatch, getState, () => {
        if (services.saveUseCaseConfiguration) {
          handleEditNotAligningPrompt(dispatch, getState, {
            saveUseCaseConfiguration: services.saveUseCaseConfiguration,
          }).catch(() => {
            // Handle error silently
          });
        }
      }),

    connection_save_call: (context?: ButtonContext) =>
      withStepContext(context, dispatch, getState, () => {
        if (services.saveUseCaseConfiguration) {
          handleEditNotAligningPrompt(dispatch, getState, {
            saveUseCaseConfiguration: services.saveUseCaseConfiguration,
          }).catch(() => {
            // Handle error silently
          });
        }
      }),

    connection_not_align_with_prev_instruction: (context?: ButtonContext) =>
      withStepContext(context, dispatch, getState, () => {
        addUserResponse(dispatch, 'No', 'userResponseNo');

        const connectionHelperMessage = {
          id: `connection-helper-modify-${Date.now()}`,
          type: 'ai_response' as const,
          subType: 'threeButtonsHelperText' as const,
          timestamp: Date.now(),
          buttons: [
            { label: 'Specify Prompt', action: 'connection_help_yes' },
            {
              label: 'Upload Browser Recording',
              action: 'connection_help_yes',
            },
            { label: 'Skip for Now', action: 'cancel' },
          ],
        };

        dispatch(addMessage(connectionHelperMessage));
      }),

    file_instruction_skip: async (context?: ButtonContext) =>
      withStepContext(context, dispatch, getState, async () => {
        const state = getState() as {
          copilot?: { navigation?: { activeStep?: string } };
        };
        const activeStep = state.copilot?.navigation?.activeStep;

        if (activeStep === 'Connection') {
          await handleStepHelpNo(
            dispatch,
            getState,
            services,
            STEP_CONFIGS.CONNECTION
          );
        } else if (
          [
            'CreateAccount',
            'UpdateAccount',
            'EnableAccount',
            'DisableAccount',
            'RemoveAccount',
          ].includes(activeStep || '')
        ) {
          await handleAccountHelpNo(dispatch, getState, services);
        }
      }),
  };

  return handlers[buttonId];
};

/**
 * Handle button click with error handling
 */
export const handleButtonClick = (
  buttonId: string,
  buttonContext: ButtonContext | undefined,
  dispatch: Dispatch,
  getState: () => unknown,
  services: ApiServices,
  callbacks: {
    showFinalConfirmation: () => void;
    handleConnectionHelpNo: () => Promise<void>;
    handleAllAccountHelpNo: () => Promise<void>;
    handleAddAccessUploadYes: () => void;
    handleRemoveAccessUploadYes: () => void;
    saveUseCaseConfiguration?: (payload: unknown) => Promise<unknown>;
  },
  controller?: ControllerInstance
): void => {
  const handler = getButtonHandler(
    buttonId,
    dispatch,
    getState,
    services,
    callbacks,
    controller
  );

  if (handler) {
    const result = handler(buttonContext);

    if (result instanceof Promise) {
      result.catch(() => {
        // Handle async errors silently
      });
    }
  }
};
